import type { LucideIcon } from "lucide-react";
import {
  TrendingUp,
  Percent,
  PiggyBank,
  Target,
  TrendingDown,
  CreditCard,
} from "lucide-react";

export type ChangeFrequency = "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";

export interface RouteEntry {
  path: string;
  priority: number;
  changeFrequency: ChangeFrequency;
}

export const ROUTES: RouteEntry[] = [
  { path: "/", priority: 1, changeFrequency: "weekly" },
  { path: "/about", priority: 0.5, changeFrequency: "yearly" },
  { path: "/privacy", priority: 0.3, changeFrequency: "yearly" },
  { path: "/calculators/compound-interest", priority: 0.9, changeFrequency: "monthly" },
  { path: "/calculators/roi", priority: 0.9, changeFrequency: "monthly" },
  { path: "/calculators/retirement", priority: 0.9, changeFrequency: "monthly" },
  { path: "/calculators/savings-goal", priority: 0.9, changeFrequency: "monthly" },
  { path: "/calculators/inflation", priority: 0.9, changeFrequency: "monthly" },
  { path: "/calculators/debt-payoff", priority: 0.9, changeFrequency: "monthly" },
  { path: "/calculators/investment", priority: 0.9, changeFrequency: "monthly" },
];

export interface CalcLink {
  id: string;
  href: string;
  navLabel: string;
  title: string;
  description: string;
  icon: LucideIcon;
}

export const CALC_LINKS: CalcLink[] = [
  {
    id: "compound-interest",
    href: "/calculators/compound-interest",
    navLabel: "Compound Interest",
    title: "Compound Interest Calculator",
    description: "See how your money grows with compound interest over time.",
    icon: TrendingUp,
  },
  {
    id: "roi",
    href: "/calculators/roi",
    navLabel: "ROI",
    title: "ROI Calculator",
    description: "Calculate return on investment, annualized ROI, and payback period.",
    icon: Percent,
  },
  {
    id: "retirement",
    href: "/calculators/retirement",
    navLabel: "Retirement",
    title: "Retirement Calculator",
    description: "Project your balance at retirement with savings and contributions.",
    icon: PiggyBank,
  },
  {
    id: "savings-goal",
    href: "/calculators/savings-goal",
    navLabel: "Savings Goal",
    title: "Savings Goal Calculator",
    description: "Find how much to save each month to reach your goal.",
    icon: Target,
  },
  {
    id: "inflation",
    href: "/calculators/inflation",
    navLabel: "Inflation",
    title: "Inflation Calculator",
    description: "See how inflation affects purchasing power over time.",
    icon: TrendingDown,
  },
  {
    id: "debt-payoff",
    href: "/calculators/debt-payoff",
    navLabel: "Debt Payoff",
    title: "Debt Payoff Calculator",
    description: "Plan payoff date and total interest on loans.",
    icon: CreditCard,
  },
  {
    id: "investment",
    href: "/calculators/investment",
    navLabel: "Investment",
    title: "Investment Calculator",
    description: "Project portfolio growth with lump sum and contributions.",
    icon: TrendingUp,
  },
];

/** Breadcrumb nav items for calculator pages: Home > Calculators > [Calculator Title] */
export function getCalculatorBreadcrumbNav(calcId: string): { name: string; href?: string }[] {
  const calc = CALC_LINKS.find((c) => c.id === calcId);
  if (!calc) return [{ name: "Home", href: "/" }];
  return [
    { name: "Home", href: "/" },
    { name: "Calculators", href: "/" },
    { name: calc.title },
  ];
}

/** Links for footer: calculators + About + Privacy */
export const FOOTER_LINKS = [
  ...CALC_LINKS.map((c) => ({ href: c.href, label: c.title })),
  { href: "/about", label: "About" },
  { href: "/privacy", label: "Privacy" },
];
